import { useEffect, useState } from 'react';
import { ApiError, getTrends } from '../api/client';
import type { TrendsResponse } from '../types';
import { formatManPerSqm } from '../lib/format';

interface Props {
  municipalityCode: string;
  municipalityName: string;
  propertyType: 'condo' | 'house';
}

/**
 * ㎡ unit-price line for the matched municipality. Deliberately uses no chart
 * library — a 20-point series renders fine as a raw <polyline> and adds zero
 * new deps to the bundle.
 *
 * Drawn in the same ink as 価格帯スケール, with no fill gradient: the series
 * is a line of medians, and shading the area under it would imply a volume
 * the numbers don't describe.
 */
export default function TrendChart({ municipalityCode, municipalityName, propertyType }: Props) {
  const [data, setData] = useState<TrendsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!municipalityCode) return;
    setLoading(true);
    setError(null);
    getTrends(municipalityCode, propertyType)
      .then((r) => setData(r))
      .catch((e) => {
        const msg = e instanceof ApiError ? e.message : (e as Error).message;
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [municipalityCode, propertyType]);

  if (loading) {
    return <p className="text-[13px] text-ink-3">推移を読み込んでいます</p>;
  }
  if (error) {
    return (
      <p className="border-l-2 border-debit pl-3 text-[13px] text-ink-2">
        推移を取得できませんでした。{error}
      </p>
    );
  }
  if (!data || data.periods.length === 0) {
    return (
      <p className="text-[13px] leading-jp text-ink-3">
        {municipalityName} は取引件数が少なく、推移を表示できません。
      </p>
    );
  }

  const min = Math.min(...data.medianPricePerSqm);
  const max = Math.max(...data.medianPricePerSqm);
  const range = Math.max(1, max - min);
  const W = 320;
  const H = 96;
  const padX = 2;
  const padTop = 8;
  const padBottom = 18;
  const innerW = W - 2 * padX;
  const innerH = H - padTop - padBottom;

  const points = data.medianPricePerSqm.map((v, i) => {
    const x = padX + (innerW * i) / Math.max(1, data.periods.length - 1);
    const y = padTop + innerH - ((v - min) / range) * innerH;
    return [x, y] as const;
  });
  const path = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

  const last = data.medianPricePerSqm[data.medianPricePerSqm.length - 1];
  const first = data.medianPricePerSqm[0];
  const growth = ((last - first) / first) * 100;
  const firstPeriod = data.periods[0];
  const lastPeriod = data.periods[data.periods.length - 1];

  return (
    <div>
      <p className="text-[12px] leading-jp text-ink-3">
        {municipalityName} ・ {propertyType === 'condo' ? '中古マンション' : '戸建て'} ・
        ㎡ 単価の中央値 ・ 取引 <span className="fig text-ink-2">{data.totalCount.toLocaleString()}</span> 件
      </p>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 w-full h-auto"
        role="img"
        aria-label={`${firstPeriod} から ${lastPeriod} までの ㎡ 単価中央値の推移。${formatManPerSqm(first)} 万円から ${formatManPerSqm(last)} 万円へ ${growth.toFixed(1)} パーセントの変化。`}
      >
        <line
          x1={padX} y1={padTop + innerH} x2={W - padX} y2={padTop + innerH}
          stroke="var(--mk-rule)" strokeWidth={1}
        />
        <polyline
          points={path}
          fill="none"
          stroke="var(--mk-primary)"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Only the endpoints get a dot — the intermediate quarters are read
            from the line, and marking all 20 turns the series into beads. */}
        <circle cx={points[0][0]} cy={points[0][1]} r={2.5} fill="var(--mk-primary)" />
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r={3}
          fill="var(--mk-accent)"
        />
        <text x={padX} y={H - 4} fontSize="9" fill="var(--mk-ink-3)">{firstPeriod}</text>
        <text x={W - padX} y={H - 4} fontSize="9" textAnchor="end" fill="var(--mk-ink-3)">
          {lastPeriod}
        </text>
      </svg>

      <dl className="mt-3 grid grid-cols-3 gap-x-4 border-t border-rule pt-2.5 text-[11px] text-ink-3">
        <Stat label={`${firstPeriod}`} value={formatManPerSqm(first)} unit="万円/㎡" />
        <Stat label={`${lastPeriod}`} value={formatManPerSqm(last)} unit="万円/㎡" emphasis />
        <Stat
          label="変化"
          value={`${growth >= 0 ? '+' : ''}${growth.toFixed(1)}`}
          unit="%"
        />
      </dl>
    </div>
  );
}

function Stat({
  label,
  value,
  unit,
  emphasis,
}: {
  label: string;
  value: string;
  unit: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <dt className="tracking-[0.06em]">{label}</dt>
      <dd className="mt-0.5">
        <span className={`fig text-[17px] ${emphasis ? 'text-accent' : 'text-primary-text'}`}>{value}</span>
        <span className="ml-1 text-[10px]">{unit}</span>
      </dd>
    </div>
  );
}
